// frontend/src/components/common/PageError.tsx
// Page-level error display with retry functionality

import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { FormButton } from './Buttons';
import { getCustomIcon } from '../../utils/customIcons';

interface PageErrorProps {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  showIcon?: boolean;
}

const ErrorContainer = styled(Box)({
  padding: '2rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2rem',
});

const ErrorIconWrapper = styled(Box)({
  display: 'flex',
  color: colors.secondary.main,
});

const ErrorText = styled(Typography)({
  fontSize: '1.8rem',
  textAlign: 'center',
  color: colors.grey[700],
});

const RetryButton = styled(FormButton)({
  borderRadius: '2px',
  fontSize: '1.6rem',
  backgroundColor: colors.primary.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: colors.primary.dark,
  },
  '& .MuiButton-startIcon > svg': {
    transition: 'transform 0.2s ease',
  },
  '&:hover .MuiButton-startIcon > svg': {
    transform: 'rotate(-45deg)',
  },
});

export const PageError: React.FC<PageErrorProps> = ({
  message,
  onRetry,
  retryLabel = 'Retry Loading',
  showIcon = true,
}) => {
  return (
    <ErrorContainer>
      {showIcon && (
        <ErrorIconWrapper>
          {getCustomIcon('error', '5rem', colors.secondary.main)}
        </ErrorIconWrapper>
      )}
      <ErrorText>{message}</ErrorText>
      {onRetry && (
        <RetryButton
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          minWidth="336px"
        >
          {retryLabel}
        </RetryButton>
      )}
    </ErrorContainer>
  );
};

export default PageError;
